import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ActionButton,
  Alert,
  Box,
  Button,
  Checkbox,
  DataTable,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  HairlineCard,
  IconButton,
  Icons,
  MenuItem,
  RowAction,
  RowActions,
  Stack,
  StatusChip,
  TextField,
  Typography,
  iconSize,
  statusTone,
  useRowActionsColumn,
} from '@munifocus/ui';
import {
  PERMIT_CATEGORIES,
  PERMIT_FIELD_TYPES,
  permitTypeProblems,
  type PermitDocumentRequirement,
  type PermitFieldDefinition,
  type PermitTypeView,
  type SavePermitTypeDto,
} from '@munifocus/contracts-gov';
import { useMutationToast } from '../../lib/mutation-toast';
import {
  formatFee,
  useInspectionTemplates,
  useModuleSwitches,
  usePermitTypes,
  useSavePermitType,
} from '../../lib/permits-api';

type Draft = SavePermitTypeDto & { id?: string };

const EMPTY: Draft = {
  key: '',
  category: 'business_licence',
  nameEn: '',
  nameFr: '',
  descriptionEn: '',
  descriptionFr: '',
  fields: [],
  documentRequirements: [],
  feeCents: 0,
  validityMonths: 12,
  renewalReminderDays: 30,
  numberPrefix: '',
  requiresInspection: false,
  inspectionTemplateId: null,
  isActive: true,
  sortOrder: 0,
};

function toDraft(t: PermitTypeView): Draft {
  return {
    id: t.id,
    key: t.key,
    category: t.category,
    nameEn: t.nameEn,
    nameFr: t.nameFr,
    descriptionEn: t.descriptionEn,
    descriptionFr: t.descriptionFr,
    fields: t.fields,
    documentRequirements: t.documentRequirements,
    feeCents: t.feeCents,
    validityMonths: t.validityMonths,
    renewalReminderDays: t.renewalReminderDays,
    numberPrefix: t.numberPrefix,
    requiresInspection: t.requiresInspection,
    inspectionTemplateId: t.inspectionTemplateId,
    isActive: t.isActive,
    sortOrder: t.sortOrder,
  };
}

/** `value|English|Français` per line, for a select field's options. */
function optionsToText(options: PermitFieldDefinition['options']): string {
  return (options ?? []).map((o) => `${o.value}|${o.labelEn}|${o.labelFr}`).join('\n');
}
function textToOptions(text: string): PermitFieldDefinition['options'] {
  return text
    .split('\n')
    .map((line) => line.split('|').map((p) => p.trim()))
    .filter((parts) => parts[0])
    .map(([value, labelEn = '', labelFr = '']) => ({ value, labelEn, labelFr }));
}

/**
 * The permit and licence types the municipality offers: what each asks the
 * applicant, which documents it needs, its fee, how long the permit lasts and
 * how its numbers read. Saved types apply to new applications only.
 */
export function PermitTypesPanel({ lg }: { lg: string }) {
  const { t, i18n } = useTranslation();
  const toast = useMutationToast();
  const actionsColumn = useRowActionsColumn();
  const types = usePermitTypes(lg);
  const modules = useModuleSwitches();
  const templates = useInspectionTemplates(modules.inspections ? lg : null);
  const save = useSavePermitType(lg);
  const [draft, setDraft] = useState<Draft | null>(null);
  const fr = i18n.language.startsWith('fr');

  const problems = draft
    ? [
        ...permitTypeProblems(draft),
        ...(draft.key && /^[a-z][a-z0-9_]{0,63}$/.test(draft.key) ? [] : ['key']),
        ...(draft.nameEn.trim() && draft.nameFr.trim() ? [] : ['name']),
        ...(/^[A-Z0-9]{1,12}$/.test(draft.numberPrefix) ? [] : ['prefix']),
      ]
    : [];

  const patch = (p: Partial<Draft>) => setDraft((d) => (d ? { ...d, ...p } : d));
  const patchField = (i: number, p: Partial<PermitFieldDefinition>) =>
    patch({ fields: draft!.fields.map((f, j) => (j === i ? { ...f, ...p } : f)) });
  const patchDoc = (i: number, p: Partial<PermitDocumentRequirement>) =>
    patch({
      documentRequirements: draft!.documentRequirements.map((d, j) =>
        j === i ? { ...d, ...p } : d,
      ),
    });

  return (
    <>
      <DataTable
        rows={types.data ?? []}
        getRowKey={(r) => r.id}
        isLoading={types.isLoading}
        emptyLabel={t('gov.permits.types.empty')}
        onRowClick={(r) => setDraft(toDraft(r))}
        action={
          <ActionButton
            label={t('gov.permits.types.new')}
            icon={Icons.Plus}
            onClick={() => setDraft({ ...EMPTY })}
          />
        }
        columns={[
          {
            key: 'name',
            label: t('gov.permits.types.name'),
            primary: true,
            sortValue: (r) => (fr ? r.nameFr : r.nameEn),
            cell: (r) => (
              <Stack>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {fr ? r.nameFr : r.nameEn}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {t(`gov.permits.categories.${r.category}`)} · {r.numberPrefix}
                </Typography>
              </Stack>
            ),
          },
          {
            key: 'fee',
            label: t('gov.permits.fields.fee'),
            sortValue: (r) => r.feeCents,
            cell: (r) => formatFee(r.feeCents, i18n.language),
          },
          {
            key: 'validity',
            label: t('gov.permits.types.validity'),
            sortValue: (r) => r.validityMonths ?? 9999,
            cell: (r) =>
              r.validityMonths
                ? t('gov.permits.types.months', { count: r.validityMonths })
                : t('gov.permits.noExpiry'),
          },
          {
            key: 'active',
            label: t('gov.permits.fields.status'),
            sortValue: (r) => (r.isActive ? 0 : 1),
            cell: (r) => (
              <StatusChip
                status={statusTone.enabled[r.isActive ? 'active' : 'inactive']}
                label={t(r.isActive ? 'gov.permits.types.active' : 'gov.permits.types.inactive')}
              />
            ),
          },
          {
            ...actionsColumn,
            cell: (r) => (
              <RowActions>
                <RowAction
                  label={t('gov.permits.types.edit')}
                  icon={Icons.Pencil}
                  onClick={() => setDraft(toDraft(r))}
                />
              </RowActions>
            ),
          },
        ]}
      />

      <Dialog open={Boolean(draft)} onClose={() => setDraft(null)} fullWidth maxWidth="md">
        {draft && (
          <>
            <DialogTitle>
              {draft.id ? t('gov.permits.types.editTitle') : t('gov.permits.types.new')}
            </DialogTitle>
            <DialogContent>
              <Stack spacing={2} sx={{ pt: 1 }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <TextField
                    label={t('gov.permits.types.nameEn')}
                    value={draft.nameEn}
                    onChange={(e) => patch({ nameEn: e.target.value })}
                    required
                    fullWidth
                  />
                  <TextField
                    label={t('gov.permits.types.nameFr')}
                    value={draft.nameFr}
                    onChange={(e) => patch({ nameFr: e.target.value })}
                    required
                    fullWidth
                  />
                </Stack>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <TextField
                    label={t('gov.permits.types.key')}
                    value={draft.key}
                    onChange={(e) => patch({ key: e.target.value.toLowerCase() })}
                    helperText={t('gov.permits.types.keyHelp')}
                    disabled={Boolean(draft.id)}
                    required
                    fullWidth
                  />
                  <TextField
                    select
                    label={t('gov.permits.types.category')}
                    value={draft.category}
                    onChange={(e) => patch({ category: e.target.value as Draft['category'] })}
                    fullWidth
                  >
                    {PERMIT_CATEGORIES.map((c) => (
                      <MenuItem key={c} value={c}>
                        {t(`gov.permits.categories.${c}`)}
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    label={t('gov.permits.types.prefix')}
                    value={draft.numberPrefix}
                    onChange={(e) => patch({ numberPrefix: e.target.value.toUpperCase() })}
                    helperText={t('gov.permits.types.prefixHelp')}
                    required
                    fullWidth
                  />
                </Stack>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <TextField
                    label={t('gov.permits.types.descriptionEn')}
                    value={draft.descriptionEn ?? ''}
                    onChange={(e) => patch({ descriptionEn: e.target.value })}
                    multiline
                    minRows={2}
                    fullWidth
                  />
                  <TextField
                    label={t('gov.permits.types.descriptionFr')}
                    value={draft.descriptionFr ?? ''}
                    onChange={(e) => patch({ descriptionFr: e.target.value })}
                    multiline
                    minRows={2}
                    fullWidth
                  />
                </Stack>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <TextField
                    type="number"
                    label={t('gov.permits.types.fee')}
                    value={(draft.feeCents / 100).toString()}
                    onChange={(e) =>
                      patch({
                        feeCents: Math.max(0, Math.round(Number(e.target.value) * 100) || 0),
                      })
                    }
                    slotProps={{ htmlInput: { min: 0, step: '0.01' } }}
                    fullWidth
                  />
                  <TextField
                    type="number"
                    label={t('gov.permits.types.validityMonths')}
                    value={draft.validityMonths ?? ''}
                    onChange={(e) =>
                      patch({
                        validityMonths: e.target.value ? Math.max(1, Number(e.target.value)) : null,
                      })
                    }
                    helperText={t('gov.permits.types.validityHelp')}
                    slotProps={{ htmlInput: { min: 1, max: 120 } }}
                    fullWidth
                  />
                  <TextField
                    type="number"
                    label={t('gov.permits.types.reminderDays')}
                    value={draft.renewalReminderDays}
                    onChange={(e) =>
                      patch({ renewalReminderDays: Math.max(0, Number(e.target.value) || 0) })
                    }
                    slotProps={{ htmlInput: { min: 0, max: 365 } }}
                    fullWidth
                  />
                </Stack>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={draft.requiresInspection}
                        onChange={(e) => patch({ requiresInspection: e.target.checked })}
                      />
                    }
                    label={t('gov.permits.types.requiresInspection')}
                  />
                  {modules.inspections && (
                    <TextField
                      select
                      label={t('gov.permits.types.template')}
                      value={draft.inspectionTemplateId ?? ''}
                      onChange={(e) => patch({ inspectionTemplateId: e.target.value || null })}
                      sx={{ minWidth: 260 }}
                    >
                      <MenuItem value="">{t('gov.permits.types.noTemplate')}</MenuItem>
                      {(templates.data ?? []).map((tpl) => (
                        <MenuItem key={tpl.id} value={tpl.id}>
                          {fr ? tpl.nameFr : tpl.nameEn}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={draft.isActive}
                        onChange={(e) => patch({ isActive: e.target.checked })}
                      />
                    }
                    label={t('gov.permits.types.takingApplications')}
                  />
                </Stack>

                <Divider />
                <Typography variant="subtitle2">{t('gov.permits.types.formTitle')}</Typography>
                {draft.fields.map((f, i) => (
                  <HairlineCard key={i} sx={{ p: 2 }}>
                    <Stack spacing={1.5}>
                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                        <TextField
                          size="small"
                          label={t('gov.permits.types.key')}
                          value={f.key}
                          onChange={(e) => patchField(i, { key: e.target.value.toLowerCase() })}
                        />
                        <TextField
                          select
                          size="small"
                          label={t('gov.permits.types.fieldType')}
                          value={f.type}
                          onChange={(e) =>
                            patchField(i, { type: e.target.value as PermitFieldDefinition['type'] })
                          }
                          sx={{ minWidth: 160 }}
                        >
                          {PERMIT_FIELD_TYPES.map((ft) => (
                            <MenuItem key={ft} value={ft}>
                              {t(`gov.permits.fieldTypes.${ft}`)}
                            </MenuItem>
                          ))}
                        </TextField>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={f.required}
                              onChange={(e) => patchField(i, { required: e.target.checked })}
                            />
                          }
                          label={t('gov.permits.types.required')}
                        />
                        <Box sx={{ flex: 1 }} />
                        <IconButton
                          aria-label={t('gov.permits.types.removeField')}
                          onClick={() => patch({ fields: draft.fields.filter((_, j) => j !== i) })}
                        >
                          <Icons.Trash2 size={iconSize.sm} />
                        </IconButton>
                      </Stack>
                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                        <TextField
                          size="small"
                          label={t('gov.permits.types.labelEn')}
                          value={f.labelEn}
                          onChange={(e) => patchField(i, { labelEn: e.target.value })}
                          fullWidth
                        />
                        <TextField
                          size="small"
                          label={t('gov.permits.types.labelFr')}
                          value={f.labelFr}
                          onChange={(e) => patchField(i, { labelFr: e.target.value })}
                          fullWidth
                        />
                      </Stack>
                      {f.type === 'select' && (
                        <TextField
                          size="small"
                          label={t('gov.permits.types.options')}
                          helperText={t('gov.permits.types.optionsHelp')}
                          value={optionsToText(f.options)}
                          onChange={(e) =>
                            patchField(i, { options: textToOptions(e.target.value) })
                          }
                          multiline
                          minRows={2}
                          fullWidth
                        />
                      )}
                    </Stack>
                  </HairlineCard>
                ))}
                <Box>
                  <Button
                    startIcon={<Icons.Plus size={iconSize.sm} />}
                    onClick={() =>
                      patch({
                        fields: [
                          ...draft.fields,
                          { key: '', type: 'text', labelEn: '', labelFr: '', required: true },
                        ],
                      })
                    }
                  >
                    {t('gov.permits.types.addField')}
                  </Button>
                </Box>

                <Divider />
                <Typography variant="subtitle2">{t('gov.permits.types.documentsTitle')}</Typography>
                {draft.documentRequirements.map((d, i) => (
                  <Stack key={i} direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                    <TextField
                      size="small"
                      label={t('gov.permits.types.key')}
                      value={d.key}
                      onChange={(e) => patchDoc(i, { key: e.target.value.toLowerCase() })}
                    />
                    <TextField
                      size="small"
                      label={t('gov.permits.types.labelEn')}
                      value={d.labelEn}
                      onChange={(e) => patchDoc(i, { labelEn: e.target.value })}
                      fullWidth
                    />
                    <TextField
                      size="small"
                      label={t('gov.permits.types.labelFr')}
                      value={d.labelFr}
                      onChange={(e) => patchDoc(i, { labelFr: e.target.value })}
                      fullWidth
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={d.required}
                          onChange={(e) => patchDoc(i, { required: e.target.checked })}
                        />
                      }
                      label={t('gov.permits.types.required')}
                    />
                    <IconButton
                      aria-label={t('gov.permits.types.removeDocument')}
                      onClick={() =>
                        patch({
                          documentRequirements: draft.documentRequirements.filter(
                            (_, j) => j !== i,
                          ),
                        })
                      }
                    >
                      <Icons.Trash2 size={iconSize.sm} />
                    </IconButton>
                  </Stack>
                ))}
                <Box>
                  <Button
                    startIcon={<Icons.Plus size={iconSize.sm} />}
                    onClick={() =>
                      patch({
                        documentRequirements: [
                          ...draft.documentRequirements,
                          { key: '', labelEn: '', labelFr: '', required: true },
                        ],
                      })
                    }
                  >
                    {t('gov.permits.types.addDocument')}
                  </Button>
                </Box>
                {problems.length > 0 && (
                  <Alert severity="warning">{t('gov.permits.types.incomplete')}</Alert>
                )}
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDraft(null)}>{t('common.actions.cancel')}</Button>
              <Button
                variant="contained"
                disabled={save.isPending || problems.length > 0}
                onClick={async () => {
                  try {
                    await save.mutateAsync({
                      ...draft,
                      validityMonths: draft.validityMonths ?? undefined,
                      inspectionTemplateId: draft.inspectionTemplateId ?? undefined,
                    });
                    toast.success('gov.permits.types.saved');
                    setDraft(null);
                  } catch (err) {
                    toast.error(err, 'gov.admin.errors.actionFailed');
                  }
                }}
              >
                {t('common.actions.save')}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </>
  );
}
